import { BOOLEAN_ATTRIBUTE_TYPES } from "#ce/utils/props";

/** Adds Vue property prefixes to non-String props while preserving booleanAttribute bindings. */
export function propertyBindings(bindings, definitions) {
  return Object.fromEntries(
    Object.entries(bindings ?? {}).map(([name, value]) => {
      const property = name.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      const definition = definitions[property];
      const type = definition?.type ?? definition;
      const types = Array.isArray(type) ? type : [type];
      const attributeBoolean = type === BOOLEAN_ATTRIBUTE_TYPES;
      const typed = !attributeBoolean && types.some((type) => type && type !== String);
      const attribute = definition ? property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`) : name;
      return [typed ? `.${property}` : attribute, value];
    }),
  );
}

/** Returns only attribute and listener bindings; the directive owns all property writes. */
export function attributeBindings(bindings, definitions) {
  return Object.fromEntries(
    Object.entries(propertyBindings(bindings, definitions)).filter(([name]) => !name.startsWith(".")),
  );
}

function writeProperties(element, { arg, value, oldValue }, initial) {
  const bindings = arg ? { [`.${arg.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())}`]: value } : value;
  const previous = arg ? { [Object.keys(bindings)[0]]: oldValue } : oldValue;
  for (const name of new Set([...Object.keys(previous ?? {}), ...Object.keys(bindings ?? {})])) {
    if (!name.startsWith(".")) {
      continue;
    }
    if (
      !initial &&
      Object.hasOwn(bindings ?? {}, name) === Object.hasOwn(previous ?? {}, name) &&
      Object.is(bindings?.[name], previous?.[name])
    ) {
      continue;
    }
    const property = name.slice(1);
    if (!customElements.get(element.localName) && property in element && !Object.hasOwn(element, property)) {
      Object.defineProperty(element, property, { configurable: true, enumerable: true, writable: true });
    }
    element[property] = bindings?.[name];
  }
}

function initializeProperties(element, binding) {
  writeProperties(element, binding, true);
}

function updateProperties(element, binding) {
  writeProperties(element, binding, false);
}

/** Writes properties directly, preserving null and undefined without Vue DOM coercion. */
export const vPreserveProperties = { created: initializeProperties, beforeUpdate: updateProperties };
